import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../lib/api'
import Layout from '../components/Layout'
import Paywall from '../components/Paywall'
import { Card, Spinner, ErrorMessage, Button, Field } from '../components/ui'
import LinkAccountForm from '../components/LinkAccountForm'
import { formatLabel, providerLabel } from '../lib/format'

// What someone is agreeing to join, shown before they create the account
// rather than weeks later when the meter runs out. Finding out a product is
// paid only at the moment it stops working is the thing this exists to
// prevent.
//
// The numbers come from /billing/price — the same endpoint the paywall reads,
// so the same react-query cache entry — and are never written down here: a
// literal price in a signup pitch is wrong from the first price change
// onwards, and would then disagree with the disclosure at checkout.
//
// Unlike the paywall, this degrades rather than disappears when Stripe cannot
// be reached. The paywall must not offer a purchase without an amount beside
// it; this offers no purchase, so saying "paid, with a free start" and leaving
// out the number is honest and still better than silence.
function PlanNotice() {
  const plan = useQuery({
    queryKey: ['billing', 'price'],
    queryFn: () => api.price(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  const amount = plan.data?.price?.formatted
  const free = plan.data?.free_evaluations

  return (
    <div className="mb-3 rounded-md border border-slate-600 bg-slate-950 p-3">
      <p className="text-sm font-semibold text-slate-100">
        {amount
          ? `Free to start, then ${amount} per year.`
          : 'Free to start, then a paid yearly subscription.'}
      </p>

      <ul className="mt-1.5 space-y-1 text-xs text-slate-400">
        <li>
          {Number.isFinite(free)
            ? `${free} free trade evaluations this season, with nothing to pay and no card to enter.`
            : 'A few free trade evaluations this season, with nothing to pay and no card to enter.'}
        </li>
        <li>
          Building trades, re-opening ones you have already evaluated, and shared links stay
          free — the subscription buys unlimited new evaluations.
        </li>
        {/* Worth stating plainly: this is a free allowance, not a free trial
            that converts. Nothing here auto-charges when it runs out, and
            saying so is the difference between a fair offer and a trap. */}
        <li>
          Nothing is charged automatically. When the free evaluations run out we ask, and the
          renewal terms are shown in full before you pay.
        </li>
      </ul>

      <p className="mt-2 text-xs text-slate-500">
        <a className="underline" href="/terms" target="_blank" rel="noreferrer">
          Terms
        </a>
        {' · '}
        <a className="underline" href="/privacy" target="_blank" rel="noreferrer">
          Privacy
        </a>
      </p>
    </div>
  )
}

// Where EmailConfirmationsController lands the browser after someone follows
// the link from their inbox.
//
// This page rather than /account because the link is routinely opened on a
// second device where nobody is signed in, and /account signed out is a stack
// of 401s. Here it reads correctly either way: signed in, above the leagues;
// signed out, above the sign-in form they are about to use.
function EmailOutcome({ outcome }) {
  if (outcome !== 'confirmed' && outcome !== 'unconfirmed') return null

  if (outcome === 'confirmed') {
    return (
      <p className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-200">
        Email address confirmed. Thanks — that is the last we will ask about it.
      </p>
    )
  }

  return (
    <p className="rounded-md border border-amber-800 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
      That confirmation link has expired or has already been replaced. Sign in and use
      “Resend the link” to get another; they are good for two days.
    </p>
  )
}

function AuthPanel() {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      mode === 'signIn' ? api.signIn(email, password) : api.signUp(email, password),
    onSuccess: () => queryClient.invalidateQueries(),
  })

  return (
    <Card
      title={mode === 'signIn' ? 'Sign in' : 'Create an account'}
      subtitle="Your leagues stay tied to your account."
      className="mx-auto max-w-sm"
    >
      {/* Only when creating an account. Someone signing back in has already
          been told, and has a live meter in the nav besides. */}
      {mode === 'signUp' && <PlanNotice />}

      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault()
          submit.mutate()
        }}
      >
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <ErrorMessage error={submit.error} />

        <Button type="submit" disabled={submit.isPending} className="w-full">
          {submit.isPending ? 'Working…' : mode === 'signIn' ? 'Sign in' : 'Sign up'}
        </Button>
      </form>

      <button
        type="button"
        className="mt-3 w-full text-xs underline text-slate-400"
        onClick={() => setMode(mode === 'signIn' ? 'signUp' : 'signIn')}
      >
        {mode === 'signIn' ? 'Need an account?' : 'Already have an account?'}
      </button>
    </Card>
  )
}

// Entering a league id by hand.
//
// **For ESPN this is the primary path, not a fallback, and it is always
// visible.** ESPN publishes no supported endpoint for "which leagues is this
// person in" — the one the app tries is undocumented, unversioned and has
// changed shape before, so its listing can be short or empty for reasons that
// have nothing to do with the user. Showing this box only when the list came
// back empty would leave someone who can see three of their four leagues with
// no route at all to the fourth.
function ManualLeagueEntry({ onConnect, pending, error }) {
  const [externalId, setExternalId] = useState('')

  return (
    <form
      className="space-y-2 rounded-md border border-slate-700 p-3"
      onSubmit={(event) => {
        event.preventDefault()
        onConnect(externalId.trim())
      }}
    >
      <label className="block text-xs text-slate-400" htmlFor="manual-league-id">
        Import by league ID
      </label>

      <div className="flex gap-2">
        <input
          id="manual-league-id"
          className="flex-1 rounded-md border border-slate-600 bg-slate-900 px-3 py-2 font-mono text-sm"
          placeholder="91163938"
          inputMode="numeric"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
        />
        <Button type="submit" disabled={pending || !externalId.trim()}>
          {pending ? 'Importing…' : 'Import'}
        </Button>
      </div>

      <p className="text-xs text-slate-500">
        The number in your league's URL:{' '}
        <span className="font-mono">fantasy.espn.com/football/league?leagueId=</span>
        <span className="font-mono text-slate-300">91163938</span>
      </p>

      <ErrorMessage error={error} />
    </form>
  )
}

function LeaguePicker({ account }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const available = useQuery({
    queryKey: ['availableLeagues', account.id],
    queryFn: () => api.availableLeagues(account.id),
  })

  const connect = useMutation({
    mutationFn: (externalId) => api.connectLeague(account.id, externalId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
      navigate(`/leagues/${data.league.id}`)
    },
  })

  // Only ESPN needs it: Sleeper's listing endpoint is supported and complete, so
  // a Sleeper account with no leagues really has none this season.
  const manual = account.provider === 'espn'

  if (available.isPending) return <Spinner label="Loading your leagues…" />
  if (available.error && !manual) return <ErrorMessage error={available.error} />

  const leagues = available.data?.available_leagues || []

  if (leagues.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-400">
          {manual
            ? "ESPN does not publish a reliable list of someone's leagues, so this can come back empty even when you have one. Enter the league ID instead."
            : `No leagues found for ${account.username} this season.`}
        </p>
        {manual && (
          <ManualLeagueEntry
            onConnect={(id) => connect.mutate(id)}
            pending={connect.isPending}
            error={connect.error}
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <ErrorMessage error={connect.error} />

      {leagues.map((league) => (
        <div
          key={league.external_id}
          className="flex items-center justify-between rounded-md px-3 py-2 border border-slate-700"
        >
          <div>
            <div className="text-sm font-medium text-slate-100">
              {league.name}
            </div>
            <div className="text-xs text-slate-400">
              {league.season} · {league.num_teams} teams
            </div>
          </div>

          <Button
            variant={league.connected ? 'secondary' : 'primary'}
            disabled={connect.isPending}
            onClick={() => connect.mutate(league.external_id)}
          >
            {connect.isPending && connect.variables === league.external_id
              ? 'Syncing…'
              : league.connected
                ? 'Re-sync'
                : 'Import'}
          </Button>
        </div>
      ))}

      {/* Always, not only when the list is empty — a partial ESPN listing is the
          case this exists for. */}
      {manual && (
        <ManualLeagueEntry
          onConnect={(id) => connect.mutate(id)}
          pending={connect.isPending}
          error={connect.error}
        />
      )}
    </div>
  )
}

// Importing a public ESPN league with nothing linked.
//
// ESPN serves a public league to anyone, so the cookies LinkAccountForm asks for
// are only needed for a private one — and asking for them up front turned away
// people who never needed to hand them over. A private league answers with an
// error that says to link, shown right here under the box.
//
// Only while no ESPN account is linked: with one, LeaguePicker's own manual
// entry reads through it, and that covers public leagues too.
//
// The iOS twin is PublicEspnImport in mobile/src/app/leagues.tsx.
function PublicEspnImport() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const connect = useMutation({
    mutationFn: (externalId) => api.importPublicLeague('espn', externalId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leagues'] })
      navigate(`/leagues/${data.league.id}`)
    },
  })

  return (
    <Card
      title="Import a public ESPN league"
      subtitle="No cookies needed if the league is public. You'll pick your team after."
    >
      <ManualLeagueEntry
        onConnect={(id) => connect.mutate(id)}
        pending={connect.isPending}
        error={connect.error}
      />
    </Card>
  )
}

export default function Leagues() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const emailOutcome = params.get('email')

  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.session(),
    retry: false,
  })

  const accounts = useQuery({
    queryKey: ['platformAccounts'],
    queryFn: () => api.platformAccounts(),
    enabled: !!session.data,
  })

  const leagues = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.leagues(),
    enabled: !!session.data,
  })

  // Shares the ['billing', 'entitlement'] key with the nav meter in Layout, so
  // this is the cached response rather than a second request.
  const entitlement = useQuery({
    queryKey: ['billing', 'entitlement'],
    queryFn: () => api.entitlement(),
    retry: false,
    enabled: !!session.data,
  })

  if (session.isPending) {
    return (
      <Layout>
        <Spinner />
      </Layout>
    )
  }

  if (session.error) {
    return (
      <Layout>
        <div className="space-y-4">
          <EmailOutcome outcome={emailOutcome} />
          <AuthPanel />
        </div>
      </Layout>
    )
  }

  // Every linked account, not just the first. Someone with a Sleeper league and
  // an ESPN league has two, and reading only [0] made the second one's leagues
  // unreachable from this page — the screen people actually import from.
  const linkedAccounts = accounts.data?.platform_accounts || []
  const connected = leagues.data?.leagues || []
  // Only for someone still on the free allowance. A subscriber has nothing to
  // buy and a comped user has nothing to pay, and both manage what they have
  // from /account.
  const meter = entitlement.data?.entitlement
  const canSubscribe = meter?.source === 'free'

  return (
    <Layout>
      <div className="space-y-4">
        <EmailOutcome outcome={emailOutcome} />

        {connected.length > 0 && (
          <Card title="Your leagues">
            <div className="space-y-2">
              {connected.map((league) => (
                <button
                  key={league.id}
                  type="button"
                  onClick={() => navigate(`/leagues/${league.id}`)}
                  className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left border border-slate-700 hover:border-slate-500"
                >
                  <div>
                    <div className="text-sm font-medium text-slate-100">
                      {league.name}
                    </div>
                    <div className="text-xs text-slate-400">
                      {league.season} · {league.num_teams} teams ·{' '}
                      {formatLabel(league.format?.ppr_type)}
                      {league.format?.superflex ? ' · Superflex' : ''}
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">Build a trade →</span>
                </button>
              ))}
            </div>
          </Card>
        )}

        {linkedAccounts.length > 0 ? (
          linkedAccounts.map((account) => (
            <Card
              key={account.id}
              title={
                linkedAccounts.length > 1
                  ? `Import from ${providerLabel(account.provider)}`
                  : 'Import a league'
              }
              subtitle={`Linked to ${providerLabel(account.provider)} as ${
                account.username || account.external_user_id
              }`}
            >
              <LeaguePicker account={account} />
            </Card>
          ))
        ) : (
          // Both providers, not just Sleeper. This is the first screen a new
          // user reaches, and offering half the providers here meant an ESPN
          // user had to find /account before they could import anything.
          <Card
            title="Connect a fantasy account"
            subtitle="Sleeper needs a username. ESPN needs two cookies, and explains why."
          >
            <LinkAccountForm
              label="Where is your league?"
              onLinked={() => {
                queryClient.invalidateQueries({ queryKey: ['platformAccounts'] })
              }}
            />
          </Card>
        )}

        {!linkedAccounts.some((account) => account.provider === 'espn') && <PublicEspnImport />}

        {/* Buying is possible from here, rather than only from the wall you hit
            when the free evaluations are gone. Last in the column on purpose:
            connecting a league is what someone came to this screen to do, and
            the offer should not be standing in front of it. */}
        {canSubscribe && <Paywall entitlement={meter} />}
      </div>
    </Layout>
  )
}
